<template>
  <v-dialog v-model="internal" max-width="1100" persistent scrollable>
    <v-card>
      <!-- Header -->
      <v-toolbar dense flat color="primary" dark>
        <v-toolbar-title>Import WiFi Overrides</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <v-card-text class="pt-6">
        <!-- Status text -->
        <div class="subtitle-2 grey--text text--darken-1 mb-2">
          <span v-if="loadingImport">Importing…</span>
          <span v-else-if="loadingValidate">Validating…</span>
          <span v-else-if="validated && validatedAt">
            Validated ✓ <span class="font-italic">{{ new Date(validatedAt).toLocaleTimeString() }}</span>
          </span>
          <span v-else-if="fileName">File ready</span>
          <span v-else>Idle</span>
        </div>

        <!-- Dropzone -->
        <div
          class="bulk-dropzone"
          @dragover.prevent
          @dragenter.prevent
          @drop.prevent="onDrop"
        >
          <div class="text-center my-6">
            <v-icon size="42">mdi-cloud-upload</v-icon>
            <div class="mt-2">Drag and drop .CSV file or</div>
            <v-btn class="mt-3" outlined @click="$refs.file.click()">Browse</v-btn>
            <input
              ref="file"
              type="file"
              accept=".csv,text/csv"
              class="d-none"
              @change="onFilePicked"
            />
          </div>
        </div>

        <!-- Secondary controls -->
        <div class="d-flex align-center mt-4">
          <v-btn small depressed class="mr-3" @click="downloadTemplate">
            <v-icon left small>mdi-information</v-icon>
            Download CSV Template
          </v-btn>

          <v-spacer />

          <v-btn v-if="fileName" small text @click="clearFile" :disabled="loadingValidate || loadingImport">
            <v-icon left small>mdi-close-circle</v-icon>
            Clear file
          </v-btn>
        </div>

        <!-- Selected file -->
        <div v-if="fileName" class="mt-3 grey--text text--darken-1">
          <v-icon small class="mr-1">mdi-file-document-outline</v-icon>
          {{ fileName }}
        </div>

        <!-- Summary + Errors -->
        <div v-if="summary" class="mt-6">
          <!-- Summary text -->
          <div class="grey--text text--darken-2">
            {{ summary.total }} total — {{ summary.valid }} valid — {{ summary.invalid }} invalid
          </div>

          <!-- Status banners -->
          <v-alert
            v-if="summary.valid > 0 && summary.invalid > 0"
            type="warning"
            dense
            outlined
            class="mt-3"
          >
            Some rows have errors. Only valid rows will be imported.
          </v-alert>

          <v-alert
            v-else-if="summary.valid > 0 && (summary.invalid === 0 || summary.invalid == null)"
            type="info"
            dense
            outlined
            class="mt-3"
          >
            All rows are valid. You can proceed to import.
          </v-alert>

          <v-alert
            v-else
            type="error"
            dense
            outlined
            class="mt-3"
          >
            No valid rows found. Fix the CSV and validate again.
          </v-alert>

          <!-- Error table (scrollable) -->
          <div
            v-if="summary.errors && summary.errors.length"
            class="errors-table-container mt-3"
          >
            <v-data-table
              dense
              :headers="errorHeaders"
              :items="summary.errors"
              :items-per-page="10"
              class="elevation-0"
              hide-default-footer
            >
              <template #item.row="{ item }"><code>{{ item.row }}</code></template>
              <template #item.message="{ item }">
                <span class="red--text">{{ item.message }}</span>
              </template>
            </v-data-table>

            <!-- Simple pagination footer -->
            <div class="d-flex align-center justify-end caption mt-2">
              Showing {{ Math.min(10, summary.errors.length) }} of {{ summary.errors.length }} errors (paginate with table props if needed)
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="loadingValidate || loadingImport" @click="close">Cancel</v-btn>

        <v-btn
          color="primary"
          class="mr-2"
          :loading="loadingValidate"
          :disabled="!canValidate"
          @click="validateCsv"
        >
          Validate (Dry run)
        </v-btn>

        <v-btn
          color="teal darken-1"
          dark
          :loading="loadingImport"
          :disabled="!canPersist"
          @click="persistCsv"
        >
          Complete Import
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'WifiOverridesBulkModalSimple',
  props: {
    value: { type: Boolean, default: false },
    orgId: { type: String, required: true },
  },
  data() {
    return {
      internal: this.value,
      file: null,
      fileName: '',
      csvText: '',
      loadingValidate: false,
      loadingImport: false,
      summary: null,
      validated: false,
      validatedAt: null,
      errorHeaders: [
        { text: 'Line', value: 'row', width: 80 },
        { text: 'Field', value: 'field', width: 140 },
        { text: 'Message', value: 'message' },
      ],
    };
  },
  computed: {
    hasCsv() {
      return this.csvText && this.csvText.trim().length > 0;
    },
    canValidate() {
      return !!this.file && !this.loadingValidate && !this.loadingImport;
    },
    canPersist() {
      return this.validated && (this.summary?.valid > 0) && !this.loadingImport;
    },
  },
  watch: {
    value(v) { this.internal = v; },
    internal(v) { this.$emit('input', v); if (!v) this.reset(); },
  },
  methods: {
    ...mapActions('wifiOverrides', ['bulkUpload']),

    reset() {
      this.file = null;
      this.fileName = '';
      this.csvText = '';
      this.loadingValidate = false;
      this.loadingImport = false;
      this.summary = null;
      this.validated = false;
      this.validatedAt = null;
    },
    close() { this.internal = false; },
    clearFile() {
      this.file = null;
      this.fileName = '';
      this.csvText = '';
      this.summary = null;
      this.validated = false;
      this.validatedAt = null;
    },

    async onFilePicked(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      await this.readFile(file);
      e.target.value = '';
    },
    async onDrop(ev) {
      const file = ev.dataTransfer?.files?.[0];
      if (!file) return;
      await this.readFile(file);
    },
    async readFile(file) {
      this.file = file;
      this.fileName = file.name;
      this.csvText = await file.text();
      this.summary = null;
      this.validated = false;
      this.validatedAt = null;
    },

    downloadTemplate() {
      const blob = new Blob(
        [
          'bssid,lat,lng\n',
          'AA11BB22CC33,35.0001,-82.0002\n',
          'BB22CC33DD44,40.7128,-74.0060\n',
        ],
        { type: 'text/csv;charset=utf-8' }
      );
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'wifi_overrides_template.csv';
      a.click();
      URL.revokeObjectURL(a.href);
    },

    // Unify response formats
    mapSummary(resp) {
      if (!resp || typeof resp !== 'object') {
        return { total: 0, valid: 0, invalid: 0, inserted: null, updated: null, skipped: null, errors: [] };
      }
      const asArray = (maybe) => (Array.isArray(maybe) ? maybe : []);
      // Variant 1: counts + errors map/array
      if (resp.counts || resp.records !== undefined || resp.successCount !== undefined) {
        const counts = resp.counts || {};
        const total = Number(counts.total ?? resp.records ?? 0) || 0;
        const valid = Number(counts.valid ?? resp.successCount ?? 0) || 0;
        const invalid = Number(counts.invalid ?? resp.errorCount ?? 0) || 0;

        // Normalize errors as map { line: [msgs] } or array
        const normalizeErrors = () => {
          if (Array.isArray(resp.errors)) return resp.errors;
          const m = resp.errors || {};
          const out = [];
          Object.keys(m).forEach(line => {
            const val = m[line];
            (Array.isArray(val) ? val : [val]).forEach(msg =>
              out.push({ row: Number(line), field: '-', message: String(msg) })
            );
          });
          return out;
        };

        // Analyze results to determine inserted vs updated
        let inserted = resp.inserted ?? counts.inserted ?? null;
        let updated = resp.updated ?? counts.updated ?? null;
        
        // If not provided, analyze results object
        if ((inserted === null || updated === null) && resp.results && typeof resp.results === 'object') {
          let insertCount = 0;
          let updateCount = 0;
          
          Object.values(resp.results).forEach(result => {
            if (result && !result.error) {
              // updatedAt null means it was an insert, otherwise it's an update
              if (result.updatedAt === null || result.updatedAt === undefined) {
                insertCount++;
              } else {
                updateCount++;
              }
            }
          });
          
          inserted = insertCount;
          updated = updateCount;
        }

        return {
          total, valid, invalid,
          inserted,
          updated,
          skipped: resp.skipped ?? counts.skipped ?? null,
          errors: normalizeErrors(),
        };
      }
      
      return {
        total: Number(resp.total) || 0,
        valid: Number(resp.valid) || 0,
        invalid: Number(resp.invalid) || 0,
        inserted: resp.inserted ?? null,
        updated: resp.updated ?? null,
        skipped: resp.skipped ?? null,
        errors: asArray(resp.errors),
      };
    },

    async validateCsv() {
      if (!this.canValidate) return;
      this.loadingValidate = true;
      try {
        const resp = await this.bulkUpload({ file: this.file, dryRun: true });
        this.summary = this.mapSummary(resp);
        this.validated = true;
        this.validatedAt = Date.now();
        this.toastSuccess(`Validation complete: ${this.summary.valid} valid, ${this.summary.invalid} invalid.`);
      } catch (e) {
        this.validated = false;
        this.validatedAt = null;
        this.toastError(e?.message || 'Validation failed');
      } finally {
        this.loadingValidate = false;
      }
    },

    async persistCsv() {
      if (!this.canPersist) return;
      this.loadingImport = true;
      try {
        const resp = await this.bulkUpload({ file: this.file, dryRun: false });
        this.summary = this.mapSummary(resp);
        const s = this.summary || {};
        this.toastSuccess(
          `Imported: inserted ${s.inserted || 0}, updated ${s.updated || 0}, invalid ${s.invalid || 0}${s.skipped != null ? `, skipped ${s.skipped}` : ''}.`
        );
        this.$emit('refresh');
        this.close();
      } catch (e) {
        this.toastError(e?.message || 'Import failed');
      } finally {
        this.loadingImport = false;
      }
    },

    // Toast helpers
    toastSuccess(msg) {
      if (this.$toasted && this.$toasted.show) {
        this.$toasted.show(msg, { position: 'bottom-right', className: ['toast-success'], duration: 1500 });
      } else {
        console.info('[toast]', msg);
      }
    },
    toastError(msg) {
      if (this.$toasted && this.$toasted.show) {
        this.$toasted.show(msg, { position: 'bottom-right', className: ['toast-error'], duration: 2500 });
      } else {
        console.error('[toast]', msg);
      }
    },
  },
};
</script>

<style scoped>
.bulk-dropzone {
  border: 2px dashed #b0bec5;
  border-radius: 8px;
  min-height: 160px;
}

.errors-table-container {
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid #ffcdd2;
  padding: 8px;
  background: #fff;
}
</style>
